import { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Card, showSnackbar } from "@cofano/cck";
import { Grid2 as Grid, CircularProgress } from "@mui/material";

import { shortid } from "imports/Utils";

import { AdminNavigationItem } from "view/admin/utils/admin.types";
import NavigationCreateEditItemDialog from "view/admin/components/dialogs/NavigationCreateEditItemDialog";

import AdminNavigationEditableList from "./AdminNavigationEditableList";
import AdminNavigationExample from "./AdminNavigationExample";

import { GetAdminNavigationDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { UpdateAdminNavigationDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

const AdminNavigation = () => {

	const [ getNavigation, { loading } ] = useLazyQuery(GetAdminNavigationDocument);
	const [ updateAdminNavigation ] = useMutation(UpdateAdminNavigationDocument);
	const [ navigationItems, setNavigationItems ] = useState<AdminNavigationItem[]>([]);

	const updateNavigationItems = (items: AdminNavigationItem[]) => {
		setNavigationItems(items.map(createUniqueId));
	};

	const [ dialog, setDialog ] = useState<{
		open: boolean;
		navigationItem?: AdminNavigationItem;
		parent?: AdminNavigationItem;
	}>({
		open: false,
		navigationItem: undefined,
	});

	const handleOnDelete = (navItem: AdminNavigationItem) => {

		const newNavigationItems = [ ...navigationItems ].filter(a => a.uniqueId !== navItem.uniqueId).map(item => {
			if (item.children && item.children.length > 0) {
				return {
					...item,
					children: item.children.filter(child => child.uniqueId !== navItem.uniqueId),
				};
			} else {
				return item;
			}
		});

		updateNavigationItems(newNavigationItems);
	};

	const handleOnEdit = (data: AdminNavigationItem) => {

		const newNavigationItems = [ ...navigationItems ].map((item): AdminNavigationItem => {
			if (item.uniqueId === data.uniqueId) {
				return data;
			} else {
				return {
					...item,
					children: item.children?.map(subItem => {
						return subItem.uniqueId === data.uniqueId ? data : subItem;
					}) ?? [],
				};
			}
		});

		updateNavigationItems(newNavigationItems);
		setDialog({ open: false, navigationItem: undefined });
	};

	const handleOnAdd = (data: AdminNavigationItem) => {
		let newNavigationItems: AdminNavigationItem[];

		if (dialog.parent) {
			newNavigationItems = [ ...navigationItems ].map((item): AdminNavigationItem => ({
				...item,
				children: item.uniqueId === dialog.parent?.uniqueId ? [ ...(item.children ?? []), data ] : item.children,
			}));

			setDialog({ open: false, navigationItem: undefined });
		} else {
			newNavigationItems = [ ...navigationItems, data ];
			setDialog({ open: false, navigationItem: undefined });
		}

		updateNavigationItems(newNavigationItems);
	};

	const getNavigationItems = () => {
		getNavigation().then(({ data }) => updateNavigationItems(data?.adminNavigation ?? []));
	};

	const handleOnSave = (list: AdminNavigationItem[]) => {

		const removeUniqueId = (currentItem: AdminNavigationItem): AdminNavigationItem => {
			const newItem: AdminNavigationItem = {
				...currentItem,
				children: currentItem.children?.map(subItem => removeUniqueId(subItem)) ?? [],
			};
			delete newItem.uniqueId;
			return newItem;
		};

		const navigation = [ ...list ].map(item => removeUniqueId(item));

		updateAdminNavigation({
			variables: {
				navigation,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			getNavigationItems();
		});
	};

	useEffect(() => {
		getNavigationItems();
	}, []);

	return (
		<div>
			{loading ? (
				<CircularProgress size={24} />
			) : (
				<Grid container spacing={3}>
					<Grid size={8}>
						<Card
							title={intl.formatMessage({ id: "ADMIN_NAVIGATION_EDIT_TITLE" })}
							actions={[ {
								text: intl.formatMessage({ id: "SAVE" }),
								onClick: () => {
									handleOnSave(navigationItems);
								},
							}, {
								text: intl.formatMessage({ id: "CANCEL" }),
								onClick: () => {
									getNavigationItems();
								},
							}, {
								text: intl.formatMessage({ id: "ADD_BUTTON" }),
								onClick: () => setDialog({ open: true, navigationItem: undefined }),
							} ]}>
							<AdminNavigationEditableList
								currentItems={navigationItems}
								onAddDropdownItem={(item) => setDialog({ open: true, navigationItem: undefined, parent: item })}
								onEdit={(item) => setDialog({ open: true, navigationItem: item })}
								onDelete={(item) => handleOnDelete(item)}
								setItems={updateNavigationItems} />
						</Card>
					</Grid>

					<Grid size={4}>
						<AdminNavigationExample navigationItems={navigationItems} />
					</Grid>
				</Grid>
			)}

			<NavigationCreateEditItemDialog
				open={dialog.open}
				navigationItem={dialog.navigationItem}
				onClose={() => setDialog({ open: false, navigationItem: undefined })}
				onEdit={(data) => handleOnEdit(data)}
				onAdd={(data) => handleOnAdd(data)} />
		</div>
	);
};

export default AdminNavigation;

const createUniqueId = (item: AdminNavigationItem): AdminNavigationItem => ({
	...item,
	uniqueId: shortid.generate(),
	children: item.children && item.children.map(subItem => createUniqueId(subItem)),
});
